<template>
  <div v-if="hasAtleastOneSeat && !hasBookedSeatsInSelection">
    <div class="font-bold p-2">Labeling</div>
    <div class="flex gap-2 p-2 text-gray-300">
      <SMButton v-tippy="'Clear row labels'" icon="deleterowlabel" :on-click="clearRowLabels" />
      <SMButton v-tippy="'Clear seat labels'" icon="deleteseatlabel" :on-click="clearSeatLabels" />
    </div>
    <div class="md-subheading px-2 my-2 bg-gray-200">Row Labels</div>
    <ul class="space-y-2 p-2">
      <li v-if="moreThanOneRowSelected" class="flex gap-2 items-center">
        <SMCheckbox
          v-if="rowsSortingType"
          v-model="rowsReverseOrder"
          v-tippy="'Reverse row labels'"
          class="p-0 m-0"
          @change="updateRowsLabels"
        ></SMCheckbox>
        <SMIcon v-else icon="row" />
        <SMSelect
          v-model="rowsSortingType"
          v-tippy="'Row labels'"
          :options="rowLabelsSortings"
          @update:model-value="onChangeRowsSortingType"
        ></SMSelect>
      </li>
      <li v-else>
        <SMInput v-model="rowLabel" v-tippy="'Row Label'" icon="text" placeholder="Text" @change="updateRowLabel" />
      </li>
      <li v-if="rowsSortingType">
        <SMInput
          v-model="prefix"
          v-tippy="'Row Prefix'"
          icon="text"
          placeholder="Row Prefix"
          @change="updateRowsLabels(rowsReverseOrder)"
        />
      </li>
      <li v-if="rowsSortingType">
        <SMInput
          v-model="startAt"
          v-tippy="'Start rows at'"
          icon="text"
          placeholder="Start rows at"
          @change="updateRowsLabels(rowsReverseOrder)"
        />
      </li>
      <li v-if="rowsSortingType">
        <SMInput
          v-model="excludeRowsString"
          v-tippy="'Exclude rows'"
          icon="text"
          placeholder="Exclude rows"
          @change="updateRowsLabels(rowsReverseOrder)"
        />
      </li>
    </ul>
    <div class="md-subheading px-2 my-2 bg-gray-200">Seat Labels</div>
    <ul class="space-y-2 p-2">
      <li v-if="moreThanOneSeatSelected" class="flex gap-2 items-center">
        <SMCheckbox
          v-if="seatsSortingType"
          v-model="seatsReverseOrder"
          v-tippy="'Reverse seat labels'"
          class="p-0 m-0"
          @change="updateSeatsLabels"
        ></SMCheckbox>
        <SMIcon v-else icon="label" />
        <SMSelect
          v-if="moreThanOneSeatSelected"
          v-model="seatsSortingType"
          v-tippy="'Seat labels'"
          :options="seatLabelsSortings"
          @update:model-value="onChangeSeatsSortingType"
        ></SMSelect>
      </li>
      <li v-else-if="!moreThanOneRowSelected">
        <SMInput v-model="seatLabel" v-tippy="'Seat Label'" icon="label" placeholder="Text" @change="updateSeatLabel" />
      </li>
      <li v-if="seatsSortingType">
        <SMInput
          v-model="seatsStartAt"
          v-tippy="'Start seats at'"
          icon="text"
          placeholder="Start seats at"
          @change="onChangeSeatsSortingType"
        />
      </li>
      <li v-if="seatsSortingType">
        <SMInput
          v-model="excludeSeatsString"
          v-tippy="'Exclude seats'"
          icon="text"
          placeholder="Exclude seats"
          @change="updateSeatsLabels(seatsReverseOrder)"
        />
      </li>
    </ul>
  </div>
</template>
<script lang="ts">
import SMButton from '@/components/ui/SMButton.vue';
import SMIcon from '@/components/ui/SMIcon.vue';
import SMInput from '@/components/ui/SMInput.vue';
import SMSelect from '@/components/ui/SMSelect.vue';
import { LABEL_TYPES, SEAT, SEATSTATUS, TYPE } from '@/constants';
import { LabelsGenerator } from '@/core/utils/labelsgenerator';
import forOwn from 'lodash/forOwn';
import merge from 'lodash/merge';
import orderBy from 'lodash/orderBy';
import sumBy from 'lodash/sumBy';
import eventbus from 'tiny-emitter/instance';
import { mapActions, mapState } from 'vuex';

export default {
  components: {
    SMIcon,
    SMInput,
    SMButton,
    SMSelect,
  },
  props: ['selection'],
  data() {
    return {
      groupedRows: null,
      rowsReverseOrder: false,
      seatsReverseOrder: false,
      skew: {
        tooltip: false,
        width: 200,
        value: 0,
        min: -5,
        max: 5,
        interval: 1,
      },
      prefix: '',
      startAt: '',
      rowLabel: '',
      seatLabel: '',
      rowsSortingType: null,
      seatsSortingType: null,
      seatsStartAt: '',
      curve: {
        tooltip: false,
        width: 200,
        value: 0,
        min: -23,
        max: 23,
        interval: 1,
      },
      rowLabelsSortings: {
        [LABEL_TYPES.LITERAL]: 'A, B, C...',
        [LABEL_TYPES.DOUBLELITERAL]: 'AA, BB, CC...',
        [LABEL_TYPES.NUMERICAL]: '1, 2, 3...',
      },
      seatLabelsSortings: {
        [LABEL_TYPES.NUMERICAL]: '1, 2, 3...',
        [LABEL_TYPES.ODD]: '1, 3, 5...',
        [LABEL_TYPES.EVEN]: '2, 4, 6...',
        [LABEL_TYPES.ODD_EVEN]: '1, 3, 5, ..., 6, 4, 2',
        [LABEL_TYPES.EVEN_ODD]: '..., 5, 3, 1, 2, 4, 6, ...',
      },
      excludeRowsString: '',
      excludeSeatsString: '',
    };
  },
  computed: {
    ...mapState('seatmap', ['conflictCheckInProgress']),
    excludeRows() {
      return this.excludeRowsString
        .split(',')
        .map((e) => e.trim())
        .map((e) => e.toUpperCase());
    },
    excludeSeats() {
      return this.excludeSeatsString
        .split(',')
        .map((e) => e.trim())
        .map((e) => e.toUpperCase());
    },
    hasAtleastOneSeat() {
      const { seats, tables } = this.getSelectedSeatsAndTables();
      const allKeys = Object.keys(tables);
      return seats.length > 0 || allKeys.length > 0;
    },
    moreThanOneSeatSelected() {
      return this.selection.length > 1 || sumBy(this.selection.items, (s) => s.seatsCount) > 1;
    },
    moreThanOneRowSelected() {
      this.groupRows();
      const rowsCount = Object.keys(this.groupedRows).length;
      return rowsCount > 1;
    },
    selectedRowItem() {
      this.groupRows();
      const groupedSeats = this.getFirstProperty(this.groupedRows);
      return this.getRowItem(groupedSeats[0]);
    },
    hasBookedSeatsInSelection() {
      let bookedUnits = 0;
      if (this.selection.items.length > 0) {
        this.selection.items.forEach((u) => {
          if (u.type === TYPE.SEAT || u.type === TYPE.TABLESEAT) {
            bookedUnits +=
              u.attributes.status === SEATSTATUS.OCCUPIED || u.attributes.status === SEATSTATUS.RESERVED || u.inCart
                ? 1
                : 0;
          } else if (u.type === TYPE.TABLE) {
            const bookedTableSeats = u.seats.filter(
              (s) =>
                s.attributes.status === SEATSTATUS.OCCUPIED || s.attributes.status === SEATSTATUS.RESERVED || s.inCart
            );
            bookedUnits += bookedTableSeats.length > 0 ? 1 : 0;
          }
        });
      }
      return !!bookedUnits;
    },
  },
  mounted() {
    if (this.hasAtleastOneSeat) {
      if (!this.moreThanOneRowSelected) {
        this.rowLabel = (this.selection.items[0].row || this.selection.items[0]).attributes.label;
        if (this.rowLabel === SEAT.LABEL) {
          this.rowLabel = '';
        }
      }
      if (!this.moreThanOneSeatSelected) {
        const type = this.selection.items[0].type;
        this.seatLabel =
          type === TYPE.TABLE
            ? this.selection.items[0].seats[0].attributes.label
            : this.selection.items[0].attributes.label;
        if (this.seatLabel === SEAT.LABEL) {
          this.seatLabel = '';
        }
      }
    }
  },
  methods: {
    ...mapActions('seatmap', ['setConflictCheckStatus']),
    recheckConflicts() {
      this.setConflictCheckStatus(true);
      setTimeout(() => {
        eventbus.emit('seatmap:recheck:conflicts');
      }, 400);
    },
    getRowItem(seat) {
      return seat.row || seat.table;
    },
    getFirstProperty(obj) {
      return obj[Object.keys(obj)[0]];
    },
    updateRowLabel() {
      if (!this.selection.length) {
        return;
      }
      this.selectedRowItem.changeLabel(this.rowLabel);
      eventbus.emit('seatmap:labels:updated');
      this.$app.seatmap.triggerUnsavedChanges(true);
    },
    updateSeatLabel() {
      if (!this.selection.length) {
        return;
      }
      if (this.selection.items[0].type === TYPE.TABLE) {
        this.selection.items[0].seats[0].changeLabel(this.seatLabel || SEAT.LABEL);
      } else {
        this.selection.items[0].changeLabel(this.seatLabel || SEAT.LABEL);
      }
      eventbus.emit('seatmap:labels:updated');
      this.$app.seatmap.triggerUnsavedChanges(true);
    },
    clearRowLabels(emitUpdateEvent = true) {
      this.rowsSortingType = null;
      this.groupRows();
      if (this.moreThanOneRowSelected) {
        forOwn(this.groupedRows, (groupedSeats) => {
          this.getRowItem(groupedSeats[0]).clearLabel();
        });
      } else {
        this.selectedRowItem.clearLabel();
      }
      if (emitUpdateEvent) {
        eventbus.emit('seatmap:labels:updated');
        this.$app.seatmap.triggerUnsavedChanges(true);
      }
    },
    clearSeatLabels() {
      this.seatsSortingType = null;
      this.groupRows();
      forOwn(this.groupedRows, (row) => {
        row.forEach((seat) => {
          seat.clearLabel();
        });
      });
      eventbus.emit('seatmap:labels:updated');
      this.$app.seatmap.triggerUnsavedChanges(true);
    },
    clearAllLabels() {
      this.clearRowLabels(false);
      this.clearSeatLabels();
    },
    onInputClick() {
      // intentionally left blank
    },
    getSelectedSeatsAndTables() {
      const seats = [];
      const tables = {};
      this.selection.items.forEach((item) => {
        if (item.row) {
          seats.push(item);
          return;
        }
        if (!item.seats || !item.seats.length) {
          return;
        }
        tables[item.uuid] = item.seats;
      });
      return {
        seats,
        tables,
      };
    },
    groupRows() {
      if (!this.groupedRows) {
        const { seats, tables } = this.getSelectedSeatsAndTables();
        this.groupedRows = merge(this.$app.seatmap.getGroupedRows(seats), tables);
      }
    },
    getRowsSortedByPositions() {
      if (this.rowsSortedByPositions) {
        return this.rowsSortedByPositions;
      }
      this.groupRows();
      const rowsPositions = [];
      forOwn(this.groupedRows, (row) => {
        let cxSum = 0;
        let cySum = 0;
        row.forEach((seat) => {
          cxSum += seat.realPosition.cx;
          cySum += seat.realPosition.cy;
        });
        rowsPositions.push({
          row,
          cx: cxSum / row.length,
          cy: cySum / row.length,
        });
      });
      this.rowsSortedByPositions = rowsPositions; //this.sortRowsByCoords(rowsPositions);
      return this.rowsSortedByPositions;
    },
    onChangeRowsSortingType() {
      this.updateRowsLabels();
    },
    updateRowsLabels(reverse = this.rowsReverseOrder) {
      if (!this.rowsSortingType) {
        return;
      }
      const rowsPositions = this.getRowsSortedByPositions();
      const labels = LabelsGenerator.getLabelsForType(
        this.rowsSortingType,
        this.startAt,
        this.prefix,
        rowsPositions.length,
        reverse,
        this.excludeRows
      );
      rowsPositions.forEach((rowPos, i) => {
        const rowItem = this.getRowItem(rowPos.row[0]);
        rowItem.changeLabel(labels[i]);
      });
      eventbus.emit('history:snapshot', 'Row label updated');
      eventbus.emit('seatmap:labels:updated');
    },
    sortRowsByCoords(rows, reverse = false) {
      return orderBy(rows, ['cx', 'cy'], reverse ? ['desc', 'desc'] : ['asc', 'asc']);
    },
    sortSeatsByCoords(seats, reverse = false) {
      return orderBy(
        seats,
        [(e) => e.realPosition.cx, (e) => e.realPosition.cy],
        reverse ? ['desc', 'desc'] : ['asc', 'asc']
      );
    },
    sortSeatsByOrderId(seats, reverse = false) {
      return orderBy(
        seats,
        [(e) => e.attributes.orderid, (e) => e.attributes.orderid],
        reverse ? ['desc', 'desc'] : ['asc', 'asc']
      );
    },
    onChangeSeatsSortingType() {
      this.updateSeatsLabels();
    },
    updateSeatsLabels(reverse = this.seatsReverseOrder) {
      if (!this.seatsSortingType) {
        return;
      }
      this.groupRows();
      forOwn(this.groupedRows, (val, key) => {
        if (!this.seatsSorted) {
          if (val[0].type === TYPE.TABLESEAT) {
            this.groupedRows[key] = this.sortSeatsByOrderId(val);
          } else {
            this.groupedRows[key] = this.sortSeatsByCoords(val);
          }
        }
        const labels = LabelsGenerator.getLabelsForType(
          this.seatsSortingType,
          parseInt(this.seatsStartAt, 10),
          '',
          this.groupedRows[key].length,
          reverse,
          this.excludeSeats
        );
        this.groupedRows[key].forEach((seat, i) => {
          seat.changeLabel(labels[i]);
          seat.attributes.index = i;
        });
      });
      this.seatsSorted = true;
      eventbus.emit('history:snapshot', 'Seat label updated');
      eventbus.emit('seatmap:labels:updated');
    },
  },
};
</script>
<style lang="scss" scoped>
.md-input-container {
  min-height: auto !important;
  margin: 0 !important;
  padding-top: 0;
  width: 165px;
}
.md-input-focused {
  label {
    display: none;
  }
}
.additional-margin-bottom {
  margin-bottom: 1px;
}
</style>
